import React, { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Button, Table, Alert, Spinner, Form, InputGroup, Badge, Card } from 'react-bootstrap'
import { useAdminSections } from 'api/hooks/use-admin-sections'

const SectionsList = () => {
  const { data: modules, isLoading, error } = useAdminSections()
  const [searchTerm, setSearchTerm] = useState('')

  const filteredSections = useMemo(() => {
    if (!modules) return []
    return modules.filter((section) => {
      const term = searchTerm.toLowerCase()
      return (
        section.name?.toLowerCase().includes(term) ||
        section.abbreviation?.toLowerCase().includes(term) ||
        section.parent_module?.toLowerCase().includes(term)
      )
    })
  }, [modules, searchTerm])

  if (isLoading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    )
  }

  if (error) {
    return <Alert variant="danger">Error loading modules: {error.message}</Alert>
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Modules</h2>
        <Button as={Link} to="/admin/sections/new" variant="primary">
          Create Module
        </Button>
      </div>

      <Card className="mb-4">
        <Card.Body>
          <InputGroup>
            <InputGroup.Text>Search</InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search by name, abbreviation, or module..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Card.Body>
      </Card>

      <div className="mb-3">
        <strong>
          Showing {filteredSections.length} of {modules?.length || 0} modules
        </strong>
      </div>

      <Table bordered hover responsive>
        <thead>
          <tr>
            <th>Name</th>
            <th>Abbreviation</th>
            <th>Parent Module</th>
            <th>Folder</th>
            <th>Analytics</th>
            <th>Table Columns</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredSections.map((section) => (
            <tr key={section.id}>
              <td>{section.name}</td>
              <td><code>{section.abbreviation}</code></td>
              <td>
                {section.parent_module ? (
                  <Badge bg="info">{section.parent_module}</Badge>
                ) : (
                  <span className="text-muted">-</span>
                )}
              </td>
              <td>{section.folder || '-'}</td>
              <td><Badge bg="light" text="primary">{section.analytics_count}</Badge></td>
              <td><Badge bg="light" text="primary">{section.table_columns_count}</Badge></td>
              <td>
                <Button as={Link} to={`/admin/sections/${section.id}`} variant="info" size="sm" className="me-2">
                  View
                </Button>
                <Button as={Link} to={`/admin/sections/${section.id}/edit`} variant="warning" size="sm">
                  Edit
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {filteredSections.length === 0 && (
        <Alert variant="info">No modules found.</Alert>
      )}
    </div>
  )
}

export default SectionsList
